import React, { useState, useEffect, useCallback, useMemo } from "react";
import { TbPhoneCall } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import styles from "./CalendaCalendar.module.css";
import {
  format,
  addDays,
  addMonths,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import api from "../../services/api";
import { handleFeatureNotIncluded } from "../../utils/notifyPlanUpgrade";
import CustomerPicker from "../CustomerPicker/CustomerPicker";
import { IdentityBadge } from "../ui";
import { formatPhoneBR } from "../../utils/phone";
import {
  getSafeContactName,
  getSafePhone,
  getSafeUsername,
  resolveIdentityState,
} from "../../utils/identity";
import tenantCustomerService from "../../services/tenantCustomerService";
import { useAuth } from "../../auth/AuthContext";
import { getStatusesForSegment } from "../../constants/appointmentStatuses";
import CalendarGrid from "./CalendarGrid";
import CalendarMonth from "./CalendarMonth";
import CalendarHeader, { buildRangeLabel } from "./CalendarHeader";
import {
  formatTimeBRT,
  formatPriceBRL,
  dayKeyBRT,
  getProfessionalColor,
  computeGridRange,
} from "./calendarHelpers";

const MOBILE_BREAKPOINT = 768;

/** Hook: largura da viewport reativa para escolher view padrão e layout. */
function useViewport() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280,
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return { width, isMobile: width < MOBILE_BREAKPOINT };
}

const CalendarCalendar = ({
  currentDate,
  onDateChange,
  selectedProfIds = [],
  professionalColors = {},
  professionals = [],
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const tenantSegment = user?.tenant?.segment ?? null;
  const statusOptions = useMemo(
    () => getStatusesForSegment(tenantSegment),
    [tenantSegment],
  );

  const { isMobile } = useViewport();
  // Mobile arranca em "dia"; desktop/tablet em "semana".
  const [view, setView] = useState(isMobile ? "day" : "week");
  useEffect(() => {
    if (isMobile) setView("day");
  }, [isMobile]);

  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [businessHours, setBusinessHours] = useState(null);
  const [formData, setFormData] = useState({
    serviceId: "",
    professionalId: "",
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Feedback inline do fluxo "Solicitar telefone" (REQUEST_CONTACT_INFO).
  const [requestPhoneState, setRequestPhoneState] = useState({
    loading: false,
    tone: null, // "success" | "info" | "error"
    message: "",
  });

  const getTenantId = () => {
    const stored = JSON.parse(localStorage.getItem("user"));
    return stored?.tenantId;
  };

  // Serviços do tenant (popula o select do modal)
  useEffect(() => {
    api
      .get("/services")
      .then((r) => setServices(r.data))
      .catch(() => setServices([]));
  }, []);

  // Dias bloqueados
  useEffect(() => {
    api
      .get("/blocks")
      .then((r) => {
        const data = r.data.blocks ?? r.data;
        const dates = data.map((b) => b.date.split("T")[0]);
        setBlockedDates(dates);
      })
      .catch(() => setBlockedDates([]));
  }, []);

  // Expediente do tenant — define a faixa horária base da grade. Reaproveita o
  // endpoint já existente GET /tenants/me (sem criar rota nova). O shape é um
  // mapa de dia → [abertura, fechamento]; dias fechados são ausentes.
  useEffect(() => {
    api
      .get("/tenants/me")
      .then((r) => setBusinessHours(r.data?.tenant?.businessHours ?? null))
      .catch(() => setBusinessHours(null));
  }, []);

  // Agendamentos da semana atual (sempre busca a semana inteira; a view de dia
  // apenas filtra visualmente — evita re-fetch ao alternar view)
  const loadAppointments = useCallback(async () => {
    const tenantId = getTenantId();
    if (!tenantId) return;

    setLoading(true);
    // Na view Mês busca a grade mensal inteira (semanas que cobrem o mês);
    // nas demais, a semana corrente. Sempre alinhado a início de semana (seg).
    let start;
    let end;
    if (view === "month") {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      start = startOfWeek(monthStart, { weekStartsOn: 1 }).toISOString();
      end = endOfWeek(monthEnd, { weekStartsOn: 1 }).toISOString();
    } else {
      start = startOfWeek(currentDate, { weekStartsOn: 1 }).toISOString();
      end = endOfWeek(currentDate, { weekStartsOn: 1 }).toISOString();
    }

    try {
      const response = await api.get(`/appointments/${tenantId}`, {
        params: { start, end },
      });
      if (Array.isArray(response.data)) {
        setAppointments(response.data);
      }
    } catch (error) {
      console.error("Erro ao carregar agenda:", error);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [currentDate, view]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    window.addEventListener("appointmentCreated", loadAppointments);
    return () =>
      window.removeEventListener("appointmentCreated", loadAppointments);
  }, [loadAppointments]);

  const visibleProfs = useMemo(
    () =>
      formData.serviceId
        ? professionals.filter((p) =>
            p.professionalServices?.some(
              (ps) => ps.serviceId === formData.serviceId,
            ),
          )
        : professionals,
    [formData.serviceId, professionals],
  );

  useEffect(() => {
    if (!formData.serviceId) return;
    setFormData((prev) => {
      if (!prev.professionalId) return prev;
      const still = professionals.some(
        (p) =>
          p.id === prev.professionalId &&
          p.professionalServices?.some((ps) => ps.serviceId === prev.serviceId),
      );
      return still ? prev : { ...prev, professionalId: "" };
    });
  }, [formData.serviceId, professionals]);

  // ── Lógica de save/cancel/status — NÃO ALTERAR ────────────────────────────
  const handleSave = async () => {
    const tenantId = getTenantId();
    if (!tenantId) {
      alert("Erro: ID do estabelecimento não encontrado.");
      return;
    }

    if (!selectedCustomer || !formData.serviceId) {
      alert("Por favor, selecione o cliente e o serviço.");
      return;
    }

    const startDate = new Date(selectedSlot.day);
    const [slotHour, slotMin] = selectedSlot.time.split(":").map(Number);
    startDate.setHours(slotHour, slotMin, 0, 0);

    const manualStart = startDate.toISOString();

    try {
      await api.post("/appointments", {
        tenantId,
        customerId: selectedCustomer.id,
        serviceId: formData.serviceId,
        startTime: manualStart,
        ...(formData.professionalId
          ? { professionalId: formData.professionalId }
          : {}),
      });
      setSelectedSlot(null);
      setSelectedCustomer(null);
      setFormData({ serviceId: "", professionalId: "" });
      setRequestPhoneState({ loading: false, tone: null, message: "" });
      loadAppointments();
    } catch (error) {
      // 403 FEATURE_NOT_INCLUDED (Plano Atendimento — ADR-0026): mensagem
      // humana com CTA, nunca o texto cru do backend.
      if (handleFeatureNotIncluded(error)) return;
      const err = error.response?.data;
      alert(err?.message || err?.error || "Erro ao agendar");
    }
  };

  // Solicita o telefone de um contato sem número (REQUEST_CONTACT_INFO).
  const handleRequestContactInfo = async () => {
    if (!selectedCustomer?.id) return;
    setRequestPhoneState({ loading: true, tone: null, message: "" });
    try {
      const data = await tenantCustomerService.requestContactInfo(
        selectedCustomer.id,
      );
      if (
        data?.status === "unavailable" &&
        data?.reason === "bsuid_send_disabled"
      ) {
        setRequestPhoneState({
          loading: false,
          tone: "info",
          message: "Solicitar telefone estará disponível em breve.",
        });
      } else {
        setRequestPhoneState({
          loading: false,
          tone: "success",
          message:
            "Pedido enviado. O cliente vai receber um botão para compartilhar o telefone.",
        });
      }
    } catch (error) {
      const status = error.response?.status;
      const message =
        status === 409
          ? "Este contato já tem um telefone cadastrado."
          : error.response?.data?.error ||
            "Não foi possível solicitar o telefone. Tente novamente.";
      setRequestPhoneState({ loading: false, tone: "error", message });
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Deseja realmente cancelar este agendamento?")) return;

    try {
      await api.patch(`/appointments/${id}/cancel`);
      loadAppointments();
    } catch (error) {
      // 403 FEATURE_NOT_INCLUDED (Plano Atendimento — ADR-0026): mensagem
      // humana com CTA, nunca o texto cru do backend.
      if (handleFeatureNotIncluded(error)) return;
      alert("Erro ao cancelar o agendamento.");
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    setStatusLoading(true);
    try {
      await api.patch(`/appointments/${appointmentId}/status`, {
        status: newStatus,
      });
      setSelectedAppointment(null);
      loadAppointments();
    } catch (error) {
      // 403 FEATURE_NOT_INCLUDED (Plano Atendimento — ADR-0026): mensagem
      // humana com CTA, nunca o texto cru do backend.
      if (!handleFeatureNotIncluded(error)) {
        alert("Erro ao atualizar status.");
      }
    } finally {
      setStatusLoading(false);
    }
  };
  // ──────────────────────────────────────────────────────────────────────────

  const weekStartDate = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEndDate = endOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = useMemo(
    () => Array.from({ length: 6 }, (_, i) => addDays(weekStartDate, i)),
    [weekStartDate],
  );

  const days = useMemo(
    () => (view === "day" ? [currentDate] : weekDays),
    [view, currentDate, weekDays],
  );

  // Filtro por profissional aplicado uma vez (memoizado).
  const filteredAppointments = useMemo(() => {
    if (selectedProfIds.length === 0) return appointments;
    return appointments.filter((app) =>
      selectedProfIds.includes(app.professionalId),
    );
  }, [appointments, selectedProfIds]);

  // Faixa horária dinâmica da grade: businessHours ∪ todos os agendamentos
  // visíveis (memoizada). Garante que nenhum evento fora do expediente seja
  // escondido/clampado. Considera apenas os agendamentos dos dias em view.
  const { startHour: gridStartHour, endHour: gridEndHour } = useMemo(() => {
    const visibleKeys = new Set(days.map((d) => dayKeyBRT(d)));
    const inView = filteredAppointments.filter((app) =>
      visibleKeys.has(dayKeyBRT(app.startTime)),
    );
    return computeGridRange(inView, businessHours);
  }, [days, filteredAppointments, businessHours]);

  // Lista de agendamentos do dia atual — usada na view mobile (lista abaixo da grade).
  const dayListAppointments = useMemo(() => {
    const key = dayKeyBRT(currentDate);
    return filteredAppointments
      .filter((app) => dayKeyBRT(app.startTime) === key)
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  }, [filteredAppointments, currentDate]);

  const step = view === "day" ? 1 : 7;

  // Navegação: a view Mês avança/recua de mês em mês; dia/semana por dias.
  const goPrev = () =>
    onDateChange(
      view === "month"
        ? addMonths(currentDate, -1)
        : addDays(currentDate, -step),
    );
  const goNext = () =>
    onDateChange(
      view === "month" ? addMonths(currentDate, 1) : addDays(currentDate, step),
    );

  // Clique num dia da view Mês → troca para a view Dia naquela data.
  const handleMonthDayClick = (day) => {
    onDateChange(day);
    setView("day");
  };

  return (
    <div className={styles.container}>
      {loading && <div className={styles.loader}>Atualizando agenda...</div>}

      <CalendarHeader
        view={view}
        onViewChange={setView}
        rangeLabel={buildRangeLabel(view, currentDate, weekStartDate, weekEndDate)}
        onPrev={goPrev}
        onToday={() => onDateChange(new Date())}
        onNext={goNext}
        professionals={professionals}
        professionalColors={professionalColors}
      />

      {view === "month" ? (
        <CalendarMonth
          currentDate={currentDate}
          appointments={filteredAppointments}
          professionalColors={professionalColors}
          blockedDates={blockedDates}
          onDayClick={handleMonthDayClick}
        />
      ) : (
        <CalendarGrid
          view={view}
          days={days}
          appointments={filteredAppointments}
          professionalColors={professionalColors}
          statusOptions={statusOptions}
          blockedDates={blockedDates}
          gridStartHour={gridStartHour}
          gridEndHour={gridEndHour}
          onSlotClick={setSelectedSlot}
          onAppointmentClick={setSelectedAppointment}
          onBlockedLink={() => navigate("/business/blocks")}
        />
      )}

      {/* Lista de agendamentos do dia — apenas no mobile (a grade fica oculta) */}
      {isMobile && (
      <div className={styles.mobileDayList}>
        <h4 className={styles.mobileDayListTitle}>
          {format(currentDate, "EEEE, dd 'de' MMMM", { locale: ptBR })}
        </h4>
        {dayListAppointments.length === 0 ? (
          <p className={styles.mobileEmpty}>Nenhum agendamento neste dia.</p>
        ) : (
          <ul className={styles.mobileList}>
            {dayListAppointments.map((app) => (
              <li
                key={app.id}
                className={`${styles.mobileListItem}${app.status === "cancelled" ? ` ${styles.appointmentCancelled}` : ""}`}
                style={{
                  borderLeft: `4px solid ${
                    professionalColors[app.professionalId] ??
                    getProfessionalColor(app.professionalId)
                  }`,
                }}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedAppointment(app)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedAppointment(app);
                  }
                }}
              >
                <span className={styles.mobileListTime}>
                  {formatTimeBRT(app.startTime)} – {formatTimeBRT(app.endTime)}
                </span>
                <span className={styles.mobileListClient}>
                  {getSafeContactName(app.clientName, "Cliente sem nome")}
                </span>
                {app.service?.name && (
                  <span className={styles.mobileListService}>
                    {app.service.name}
                    {app.service?.price != null
                      ? ` · ${formatPriceBRL(app.service.price)}`
                      : ""}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      )}

      {/* MODAL DE DETALHE DO AGENDAMENTO */}
      {selectedAppointment && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>Agendamento</h3>
            <p>
              <strong>
                {getSafeContactName(
                  selectedAppointment.clientName,
                  "Cliente sem nome",
                )}
              </strong>
            </p>
            <p>{selectedAppointment.service?.name}</p>
            <p>
              {selectedAppointment.startTime
                ? formatTimeBRT(selectedAppointment.startTime)
                : "–"}{" "}
              –{" "}
              {selectedAppointment.endTime
                ? formatTimeBRT(selectedAppointment.endTime)
                : "–"}
            </p>
            <p>
              Telefone:{" "}
              {getSafePhone(selectedAppointment.clientPhone)
                ? formatPhoneBR(getSafePhone(selectedAppointment.clientPhone))
                : "não compartilhado"}
            </p>
            <p>
              Profissional:{" "}
              {selectedAppointment.professional?.user?.name ?? "—"}
            </p>

            <div style={{ marginTop: "1rem" }}>
              <p style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Status:</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {statusOptions.map(({ value, label }) => (
                  <button
                    key={value}
                    disabled={
                      statusLoading || selectedAppointment.status === value
                    }
                    onClick={() =>
                      handleStatusChange(selectedAppointment.id, value)
                    }
                    className={styles.statusBtn}
                    style={
                      selectedAppointment.status === value
                        ? { background: "#EBF5FB", fontWeight: 600 }
                        : undefined
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.modalButtons} style={{ marginTop: "1.5rem" }}>
              <button
                className={styles.cancelBtn}
                onClick={() => setSelectedAppointment(null)}
              >
                Fechar
              </button>
              <button
                className={styles.cancelBtn}
                style={{
                  background: "#fff5f5",
                  color: "#c53030",
                  borderColor: "#fed7d7",
                }}
                onClick={() => {
                  setSelectedAppointment(null);
                  handleCancel(selectedAppointment.id);
                }}
              >
                Cancelar agendamento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE NOVO AGENDAMENTO */}
      {selectedSlot && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>Novo Agendamento</h3>
            <p>
              {format(selectedSlot.day, "EEEE, dd 'de' MMMM", {
                locale: ptBR,
              })}{" "}
              às {selectedSlot.time}
            </p>

            {selectedCustomer === null ? (
              <button
                className={styles.input}
                style={{
                  cursor: "pointer",
                  textAlign: "left",
                  color: "#888",
                  background: "#fafafa",
                }}
                onClick={() => setPickerOpen(true)}
                aria-label="Selecionar cliente"
              >
                Selecionar cliente...
              </button>
            ) : (
              (() => {
                const selectedCustomerName = getSafeContactName(
                  selectedCustomer.name,
                  "Cliente sem nome",
                );
                const selectedUsername = getSafeUsername(
                  selectedCustomer.username,
                );
                const selectedIdentity = resolveIdentityState({
                  identityState: selectedCustomer.identityState,
                  hasHiddenPhone: selectedCustomer.hasHiddenPhone,
                  phone: selectedCustomer.phone,
                  username: selectedUsername,
                });
                const phoneHidden = selectedIdentity !== "phone";
                return (
                  <>
                    <div className={styles.selectedCustomer}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "var(--nx-s2)",
                          flexWrap: "wrap",
                        }}
                      >
                        <strong>{selectedCustomerName}</strong>
                        <IdentityBadge
                          state={selectedIdentity}
                          username={selectedUsername}
                          phone={selectedCustomer.phone}
                          size="sm"
                        />
                      </span>
                      <button
                        className={styles.swapCustomerBtn}
                        onClick={() => setPickerOpen(true)}
                        aria-label="Trocar cliente selecionado"
                      >
                        Trocar
                      </button>
                    </div>

                    {phoneHidden && (
                      <div className={styles.requestPhoneRow}>
                        <button
                          type="button"
                          className={styles.requestPhoneBtn}
                          onClick={handleRequestContactInfo}
                          disabled={requestPhoneState.loading}
                          aria-busy={requestPhoneState.loading}
                        >
                          <TbPhoneCall
                            className={styles.requestPhoneIcon}
                            aria-hidden="true"
                          />
                          {requestPhoneState.loading
                            ? "Solicitando…"
                            : "Solicitar telefone"}
                        </button>
                        {requestPhoneState.message && (
                          <p
                            className={`${styles.requestPhoneMsg} ${
                              styles[
                                `requestPhone_${requestPhoneState.tone}`
                              ] ?? ""
                            }`}
                            role="status"
                            aria-live="polite"
                          >
                            {requestPhoneState.message}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                );
              })()
            )}

            <p className={styles.customerHelpText}>
              Aceita telefone OU cliente com identidade WhatsApp (contato sem
              número).
            </p>

            <select
              className={styles.input}
              value={formData.serviceId}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, serviceId: e.target.value }))
              }
              aria-label="Selecionar serviço"
            >
              <option value="">Selecione um serviço</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.duration} min)
                </option>
              ))}
            </select>

            {visibleProfs.length > 0 && (
              <select
                className={styles.input}
                value={formData.professionalId}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    professionalId: e.target.value,
                  }))
                }
                aria-label="Selecionar profissional"
              >
                <option value="">Profissional (opcional)</option>
                {visibleProfs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.user?.name ?? "Profissional sem nome"}
                  </option>
                ))}
              </select>
            )}

            <div className={styles.modalButtons}>
              <button
                className={styles.cancelBtn}
                onClick={() => {
                  setSelectedSlot(null);
                  setSelectedCustomer(null);
                  setFormData({ serviceId: "", professionalId: "" });
                  setRequestPhoneState({
                    loading: false,
                    tone: null,
                    message: "",
                  });
                }}
              >
                Cancelar
              </button>
              <button
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={!selectedCustomer}
                aria-disabled={!selectedCustomer}
                style={
                  !selectedCustomer
                    ? { opacity: 0.5, cursor: "not-allowed" }
                    : {}
                }
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOMER PICKER — abre sobre o modal de agendamento */}
      <CustomerPicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(c) => {
          setSelectedCustomer(c);
          setPickerOpen(false);
          setRequestPhoneState({ loading: false, tone: null, message: "" });
        }}
      />
    </div>
  );
};

export default CalendarCalendar;
